import axiosClient from 'lib/axiosClient';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';

const useEntryWorkspaceMutations = () => {
  const { enqueueSnackbar } = useSnackbar();

  const request = useCallback(
    (promise, { successMessage } = {}) =>
      promise
        .then(res => {
          if (res.status === 200) {
            if (successMessage) {
              enqueueSnackbar(successMessage, { variant: 'success' });
            }
            return res.data?.value ?? true;
          }
          enqueueSnackbar(res?.message, { variant: 'error' });
          return null;
        })
        .catch(err => {
          enqueueSnackbar(err?.message || err?.response?.data?.message, {
            variant: 'error',
          });
          return null;
        }),
    [enqueueSnackbar],
  );

  const insertShoppingCart = useCallback(
    payload =>
      request(
        axiosClient.post('Operation/Entry/Shoppingcart/Insert', {
          operationGuid: payload.operationGuid,
          shoppingCartGuid: payload.shoppingCartGuid,
          sectionGuid: payload.sectionGuid,
        }),
        { successMessage: 'سبد با موفقیت افزوده شد' },
      ),
    [request],
  );

  const deleteShoppingCart = useCallback(
    basketGuid =>
      request(
        axiosClient.delete('Operation/Entry/Shoppingcart/Delete', {
          params: { Guid: basketGuid },
        }),
        { successMessage: 'سبد حذف شد' },
      ),
    [request],
  );

  const removeShoppingCartTask = useCallback(
    productGuid =>
      request(
        axiosClient.delete('Operation/Entry/Shoppingcart/RemoveTask', {
          params: { Guid: productGuid },
        }),
        { successMessage: 'محصول از سبد حذف شد' },
      ),
    [request],
  );

  const runBasketTask = useCallback(
    basketGuid =>
      request(
        axiosClient.put('Operation/Entry/Task/Run', null, {
          params: { Guid: basketGuid },
        }),
        { successMessage: 'ارسال برای چیدمان انجام شد' },
      ),
    [request],
  );

  const submitOperation = useCallback(
    operationGuid =>
      request(
        axiosClient.put('Operation/Submit', null, {
          params: { GUID: operationGuid },
        }),
        { successMessage: 'عملیات با موفقیت اتمام شد' },
      ),
    [request],
  );

  const submitVoucher = useCallback(
    operationGuid =>
      request(
        axiosClient.put('Operation/Voucher/Submit', null, {
          params: { GUID: operationGuid },
        }),
        { successMessage: 'ثبت سند با موفقیت انجام شد' },
      ),
    [request],
  );

  const getZoneSections = useCallback(
    zoneGuid =>
      request(
        axiosClient.get('InventoryGeo/Section/List', {
          params: { Guid: zoneGuid },
        }),
      ),
    [request],
  );

  const getScannerDetails = useCallback(
    qrGuid =>
      request(
        axiosClient.get('Scaner/GetDetails', {
          params: { GUID: qrGuid },
        }),
      ),
    [request],
  );

  const getAvailableShoppingCarts = useCallback(
    (operationGuid, sectionGuid) =>
      request(
        axiosClient.get(
          'Operation/Entry/Shoppingcart/GetAllShoppingCartAvailable',
          {
            params: sectionGuid
              ? { GUID: operationGuid, SectionGuid: sectionGuid }
              : { Guid: operationGuid },
          },
        ),
      ),
    [request],
  );

  const getAvailableShoppingCartList = useCallback(
    zoneGuid =>
      request(
        axiosClient.get('InventoryShoppingCart/GetAllShoppingCartAvailable', {
          params: { guid: zoneGuid },
        }),
      ),
    [request],
  );

  const getPlacementSections = useCallback(
    (zoneGuid, productPackGuid) =>
      request(
        axiosClient.get('Inventory/ProductData/Section/GetAll', {
          params: { Guid: zoneGuid, ProductPackGuid: productPackGuid },
        }),
      ),
    [request],
  );

  const getPlacementAreas = useCallback(
    (sectionGuid, productPackGuid) =>
      request(
        axiosClient.get('Inventory/ProductData/Area/GetAll', {
          params: { Guid: sectionGuid, ProductPackGuid: productPackGuid },
        }),
      ),
    [request],
  );

  const getShelfAndPart = useCallback(
    (areaGuid, productPackGuid) =>
      request(
        axiosClient.get('Inventory/ProductData/GetShelfandPart', {
          params: { guid: areaGuid, ProductPackGuid: productPackGuid },
        }),
      ),
    [request],
  );

  const getCells = useCallback(
    (partGuid, productPackGuid) =>
      request(
        axiosClient.get('Inventory/ProductData/Cell/GetAll', {
          params: { Guid: partGuid, ProductPackGuid: productPackGuid },
        }),
      ),
    [request],
  );

  const initTask = useCallback(
    payload =>
      request(
        axiosClient.post('Operation/Entry/Task/Init', {
          operationGuid: payload.operationGuid,
          operationProductGuid: payload.operationProductGuid,
          addressGuid: payload.addressGuid,
          shoppingCartGuid: payload.shoppingCartGuid,
          count: payload.count,
        }),
        { successMessage: 'جانمایی با موفقیت ثبت شد' },
      ),
    [request],
  );

  return {
    insertShoppingCart,
    deleteShoppingCart,
    removeShoppingCartTask,
    runBasketTask,
    submitOperation,
    submitVoucher,
    getZoneSections,
    getScannerDetails,
    getAvailableShoppingCarts,
    getAvailableShoppingCartList,
    getPlacementSections,
    getPlacementAreas,
    getShelfAndPart,
    getCells,
    initTask,
  };
};

export default useEntryWorkspaceMutations;
